import { GoalMetric, GoalPeriod } from '@launchpad/shared';

/**
 * Get the start of the current period in user's local timezone.
 * @param {string} period - 'weekly' | 'monthly' | 'total'
 * @param {string} [createdAt] - goal creation date (for 'total')
 * @returns {string} ISO string of period start
 */
export function getPeriodStart(period, createdAt) {
  const now = new Date();
  if (period === GoalPeriod.MONTHLY) {
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  }
  if (period === GoalPeriod.WEEKLY) {
    const day = now.getDay(); // 0=Sun, 1=Mon...
    const diff = day === 0 ? 6 : day - 1; // days since Monday
    const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
    return monday.toISOString();
  }
  return createdAt || new Date(0).toISOString();
}

const METRIC_CONFIG = {
  [GoalMetric.APPLICATIONS_SENT]: {
    filter: (app) => app.boardType !== 'networking',
    statuses: new Set(['applied']),
    validCurrent: new Set(['applied', 'interviewing', 'offer']),
  },
  [GoalMetric.NETWORKING_OUTREACH]: {
    filter: (app) => app.boardType === 'networking',
    statuses: new Set(['outreach', 'scheduled', 'nurturing']),
    validCurrent: new Set(['outreach', 'scheduled', 'nurturing']),
  },
  [GoalMetric.CONVERSATIONS_COMPLETED]: {
    filter: (app) => app.boardType === 'networking',
    statuses: new Set(['scheduled', 'nurturing']),
    validCurrent: new Set(['scheduled', 'nurturing']),
  },
  [GoalMetric.INTERVIEWS_SCHEDULED]: {
    filter: (app) => app.boardType !== 'networking',
    statuses: new Set(['interviewing']),
    validCurrent: new Set(['interviewing', 'offer']),
  },
};

function countTransitions(applications, config, periodStart) {
  let count = 0;
  for (const app of applications) {
    if (!config.filter(app)) continue;
    // Only count if card is currently at or beyond the target status
    if (!config.validCurrent.has(app.status)) continue;
    for (const entry of app.statusHistory || []) {
      if (config.statuses.has(entry.to) && entry.timestamp >= periodStart) {
        count++;
        break; // each card counts at most once
      }
    }
  }
  return count;
}

/**
 * Compute progress for all goals.
 * @param {Array} applications
 * @param {Array} goals
 * @returns {Array<{goalId, current, target, complete, streak, lastPeriod}>}
 */
export function computeGoalProgress(applications, goals) {
  return goals.map((goal) => {
    const periodStart = getPeriodStart(goal.period, goal.createdAt);
    let current;

    if (goal.metric === GoalMetric.CUSTOM) {
      current = goal.current || 0;
    } else {
      const config = METRIC_CONFIG[goal.metric];
      current = config ? countTransitions(applications, config, periodStart) : 0;
    }

    const complete = current >= goal.target;
    const streak = computeStreak(applications, goal);
    const lastPeriod = computeLastPeriod(applications, goal);

    return { goalId: goal.goalId, current, target: goal.target, complete, streak, lastPeriod };
  });
}

function computeStreak(applications, goal) {
  if (goal.period === GoalPeriod.TOTAL || goal.metric === GoalMetric.CUSTOM) return 0;

  let streak = 0;
  const now = new Date();

  for (let i = 1; i <= 12; i++) {
    const { start, end } = getPastPeriodBounds(goal.period, now, i);
    const count = countForPeriod(applications, goal, start, end);
    if (count >= goal.target) streak++;
    else break;
  }
  return streak;
}

function computeLastPeriod(applications, goal) {
  if (goal.period === GoalPeriod.TOTAL || goal.metric === GoalMetric.CUSTOM) return null;

  const now = new Date();
  const { start, end } = getPastPeriodBounds(goal.period, now, 1);
  const count = countForPeriod(applications, goal, start, end);
  return { current: count, target: goal.target, complete: count >= goal.target };
}

function countForPeriod(applications, goal, start, end) {
  const config = METRIC_CONFIG[goal.metric];
  if (!config) return 0;
  let count = 0;
  for (const app of applications) {
    if (!config.filter(app)) continue;
    for (const entry of app.statusHistory || []) {
      if (config.statuses.has(entry.to) && entry.timestamp >= start && entry.timestamp < end) {
        count++;
        break;
      }
    }
  }
  return count;
}

function getPastPeriodBounds(period, now, periodsBack) {
  if (period === GoalPeriod.WEEKLY) {
    const day = now.getDay();
    const diff = day === 0 ? 6 : day - 1;
    const thisMonday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
    const start = new Date(thisMonday);
    start.setDate(start.getDate() - periodsBack * 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    return { start: start.toISOString(), end: end.toISOString() };
  }
  // monthly
  const start = new Date(now.getFullYear(), now.getMonth() - periodsBack, 1);
  const end = new Date(now.getFullYear(), now.getMonth() - periodsBack + 1, 1);
  return { start: start.toISOString(), end: end.toISOString() };
}
